import { Button, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  offices,
  companies,
  type OfficeCreateRequest,
  type OfficeCountry,
} from "../../../api/addressBook.service";
import { PageHeader } from "../../../components/PageHeader";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import { usePermissions } from "../../../hooks/usePermissions";
import LoadingState from "../../../components/LoadingState";
import { ROUTES } from "../../../routes";
import styles from "../AddressBook.module.css";

type OfficeFormData = {
  name: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  county: string;
  postcode: string;
  countryName: string;
  countryId: string;
  location: string;
  phone: string;
  fax: string;
  email: string;
  websiteUrl: string;
  isHeadOffice: boolean;
};

function CompanyOfficeNew() {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const [searchParams] = useSearchParams();
  const prefilledName = searchParams.get("name")?.trim() ?? "";
  const { hasAnyPermission } = usePermissions();
  const canWrite = hasAnyPermission(["addressbook.write"]);

  const [companyName, setCompanyName] = useState<string>("");
  const [countriesList, setCountriesList] = useState<OfficeCountry[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const [formData, setFormData] = useState<OfficeFormData>(() => ({
    name: prefilledName,
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    city: "",
    county: "",
    postcode: "",
    countryName: "",
    countryId: "",
    location: "",
    phone: "",
    fax: "",
    email: "",
    websiteUrl: "",
    isHeadOffice: false,
  }));
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (companyId) {
      loadCompany(companyId);
    }
    loadCountries();
  }, [companyId]);

  async function loadCompany(id: string) {
    try {
      const response = await companies.getById(id);
      setCompanyName(response.data.name);
    } catch {
      setToast({ message: "Could not load company name.", type: "warning" });
    }
  }

  async function loadCountries() {
    try {
      setCountriesLoading(true);
      setCountriesError(null);
      const countriesResponse = await offices.getCountries();
      const sorted = countriesResponse.slice().sort((a, b) => a.name.localeCompare(b.name));
      setCountriesList(sorted);
    } catch {
      setCountriesError("Failed to load countries. Please refresh the page.");
    } finally {
      setCountriesLoading(false);
    }
  }

  function handleInputChange(field: keyof OfficeFormData, value: string | boolean) {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      });
    }
  }

  function handleCountryChange(countryId: string) {
    const country = countriesList.find(c => String(c.id) === countryId);
    setFormData(prev => ({
      ...prev,
      countryId,
      countryName: country?.name || "",
    }));
    if (formErrors.countryId) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.countryId;
        return newErrors;
      });
    }
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }
    if (!formData.countryId) {
      errors.countryId = "Country is required";
    }
    if (!formData.city.trim()) {
      errors.city = "City is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (!validateForm() || !companyId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: OfficeCreateRequest = {
        name: formData.name.trim(),
        addressLine1: formData.addressLine1?.trim() || null,
        addressLine2: formData.addressLine2?.trim() || null,
        addressLine3: formData.addressLine3?.trim() || null,
        city: formData.city?.trim() || null,
        county: formData.county?.trim() || null,
        postcode: formData.postcode?.trim() || null,
        countryName: formData.countryName?.trim() || null,
        countryId: formData.countryId ? Number(formData.countryId) : null,
        location: formData.location?.trim() || null,
        phone: formData.phone?.trim() || null,
        fax: formData.fax?.trim() || null,
        email: formData.email?.trim() || null,
        websiteUrl: formData.websiteUrl?.trim() || null,
        isHeadOffice: formData.isHeadOffice ?? false,
      };

      await offices.create(companyId, payload);
      navigate(ROUTES.addressBook.companyDetail(companyId), { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create office";
      setToast({ message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    if (companyId) {
      navigate(ROUTES.addressBook.companyDetail(companyId));
    }
  }

  if (countriesLoading) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader title="New Office" subtitle="Add a new office to this company" />
        <LoadingState message="Loading..." />
      </div>
    );
  }

  return (
    <div className={styles.detailWrapper}>
      <PageHeader title="New Office" subtitle="Add a new office to this company" />

      <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
        <Link to={ROUTES.addressBook.companies} className={styles.breadcrumbLink}>
          Companies
        </Link>
        <span className={styles.breadcrumbSeparator} aria-hidden="true">
          /
        </span>
        {companyId && (
          <>
            <Link
              to={ROUTES.addressBook.companyDetail(companyId)}
              className={styles.breadcrumbLink}
            >
              {companyName || "Company"}
            </Link>
            <span className={styles.breadcrumbSeparator} aria-hidden="true">
              /
            </span>
          </>
        )}
        <span className={styles.breadcrumbCurrent}>New Office</span>
      </nav>

      <form onSubmit={handleSubmit} className={styles.formContainer}>
        {/* Row 1: Name + Head Office checkbox */}
        <div className={`${styles.formRow} ${styles.formRowNameHead}`}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="name">
              Name *
            </label>
            <input
              id="name"
              type="text"
              className={styles.formInput}
              value={formData.name}
              onChange={e => handleInputChange("name", e.target.value)}
              placeholder="Enter office name"
              disabled={!canWrite}
            />
            {formErrors.name && <span className={styles.errorText}>{formErrors.name}</span>}
          </div>
          <div className={styles.checkboxField}>
            <input
              id="isHeadOffice"
              type="checkbox"
              checked={formData.isHeadOffice}
              onChange={e => handleInputChange("isHeadOffice", e.target.checked)}
              disabled={!canWrite}
            />
            <label htmlFor="isHeadOffice">Head Office</label>
          </div>
        </div>

        {/* Row 2: Address Line 1 */}
        <div className={styles.formRow} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="addressLine1">
              Address Line 1
            </label>
            <input
              id="addressLine1"
              type="text"
              className={styles.formInput}
              value={formData.addressLine1}
              onChange={e => handleInputChange("addressLine1", e.target.value)}
              placeholder="Enter street address"
              disabled={!canWrite}
            />
          </div>
        </div>

        {/* Row 3: Address Line 2 */}
        <div className={styles.formRow} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="addressLine2">
              Address Line 2
            </label>
            <input
              id="addressLine2"
              type="text"
              className={styles.formInput}
              value={formData.addressLine2}
              onChange={e => handleInputChange("addressLine2", e.target.value)}
              placeholder="Enter address line 2"
              disabled={!canWrite}
            />
          </div>
        </div>

        {/* Row 4: Address Line 3 */}
        <div className={styles.formRow} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="addressLine3">
              Address Line 3
            </label>
            <input
              id="addressLine3"
              type="text"
              className={styles.formInput}
              value={formData.addressLine3}
              onChange={e => handleInputChange("addressLine3", e.target.value)}
              placeholder="Enter address line 3"
              disabled={!canWrite}
            />
          </div>
        </div>

        {/* Row 5: City, County, Postcode */}
        <div className={`${styles.formRow} ${styles.formRow3Col}`} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="city">
              City *
            </label>
            <input
              id="city"
              type="text"
              className={`${styles.formInput}${formErrors.city ? ` ${styles.formInputError}` : ""}`}
              value={formData.city}
              onChange={e => handleInputChange("city", e.target.value)}
              placeholder="Enter city"
              disabled={!canWrite}
            />
            {formErrors.city && <span className={styles.errorText}>{formErrors.city}</span>}
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="county">
              County
            </label>
            <input
              id="county"
              type="text"
              className={styles.formInput}
              value={formData.county}
              onChange={e => handleInputChange("county", e.target.value)}
              placeholder="Enter county"
              disabled={!canWrite}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="postcode">
              Postcode
            </label>
            <input
              id="postcode"
              type="text"
              className={styles.formInput}
              value={formData.postcode}
              onChange={e => handleInputChange("postcode", e.target.value)}
              placeholder="Enter postcode"
              disabled={!canWrite}
            />
          </div>
        </div>

        {/* Row 6: Location */}
        <div className={styles.formRow} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="location">
              Location
            </label>
            <input
              id="location"
              type="text"
              className={styles.formInput}
              value={formData.location}
              onChange={e => handleInputChange("location", e.target.value)}
              placeholder="Enter location"
              disabled={!canWrite}
            />
            <span className={styles.helperText}>
              Free-text descriptor e.g. &apos;City of London&apos;
            </span>
          </div>
        </div>

        {/* Row 7: Country */}
        <div className={styles.formRow} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="countryId">
              Country *
            </label>
            <select
              id="countryId"
              className={`${styles.formInput}${formErrors.countryId ? ` ${styles.formInputError}` : ""}`}
              value={formData.countryId}
              onChange={e => handleCountryChange(e.target.value)}
              disabled={!canWrite}
            >
              <option value="">Select a country</option>
              {countriesList.map(country => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
            {formErrors.countryId && (
              <span className={styles.errorText}>{formErrors.countryId}</span>
            )}
            {countriesError && <span className={styles.errorText}>{countriesError}</span>}
          </div>
        </div>

        {/* Row 8: Phone, Fax */}
        <div className={`${styles.formRow} ${styles.formRow2Col}`} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="phone">
              Phone
            </label>
            <input
              id="phone"
              type="tel"
              className={styles.formInput}
              value={formData.phone}
              onChange={e => handleInputChange("phone", e.target.value)}
              placeholder="+1 234 567 8900"
              disabled={!canWrite}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="fax">
              Fax
            </label>
            <input
              id="fax"
              type="tel"
              className={styles.formInput}
              value={formData.fax}
              onChange={e => handleInputChange("fax", e.target.value)}
              placeholder="Enter fax number"
              disabled={!canWrite}
            />
          </div>
        </div>

        {/* Row 9: Email, Website URL */}
        <div className={`${styles.formRow} ${styles.formRow2Col}`} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              className={styles.formInput}
              value={formData.email}
              onChange={e => handleInputChange("email", e.target.value)}
              placeholder="Enter email address"
              disabled={!canWrite}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="websiteUrl">
              Website URL
            </label>
            <input
              id="websiteUrl"
              type="url"
              className={styles.formInput}
              value={formData.websiteUrl}
              onChange={e => handleInputChange("websiteUrl", e.target.value)}
              placeholder="https://example.com"
              disabled={!canWrite}
            />
          </div>
        </div>

        <div className={styles.formActions}>
          <Button variant="outlined" color="default" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <PermissionGate permission="addressbook.write">
            <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Office"}
            </Button>
          </PermissionGate>
        </div>
      </form>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default CompanyOfficeNew;
